import { useMutation, useQueryClient } from '@tanstack/react-query';
import ContractorService, {
  IUpdateContractorParams,
} from '@clickersphere/core/services/ContractorService';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { ContractorQueryData, CONTRACTORS_QUERY_KEY } from './useContractors';

export function useUpdateContractor() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const {
    isPending: updateContractorIsPending,
    mutateAsync: updateContractorAsync,
  } = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: IUpdateContractorParams;
    }) => ContractorService.update(id, { ...data }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(CONTRACTORS_QUERY_KEY),
      });

      const oldContractors = queryClient.getQueryData<ContractorQueryData[]>(
        buildQueryKey(CONTRACTORS_QUERY_KEY),
      );

      queryClient.setQueryData<ContractorQueryData[]>(
        buildQueryKey(CONTRACTORS_QUERY_KEY),
        (old) =>
          old?.map((contractor) =>
            contractor.id === variables.id
              ? { ...contractor, ...variables.data, queryStatus: 'pending' }
              : contractor,
          ),
      );

      return { oldContractors };
    },
    onSuccess: async (_data, variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(CONTRACTORS_QUERY_KEY),
      });

      queryClient.setQueryData<ContractorQueryData[]>(
        buildQueryKey(CONTRACTORS_QUERY_KEY),
        (old) =>
          old?.map((contractor) =>
            contractor.id === variables.id
              ? { ...contractor, queryStatus: undefined }
              : contractor,
          ),
      );
    },
    onError: async (_error, _variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(CONTRACTORS_QUERY_KEY),
      });

      queryClient.setQueryData(
        buildQueryKey(CONTRACTORS_QUERY_KEY),
        context?.oldContractors,
      );
    },
  });

  return {
    updateContractorAsync,
    updateContractorIsPending,
  };
}
